import { useCallback, useEffect, useRef, useState } from 'react';
import { icons } from '@images';
import { useAppDispatch } from '@/store/hooks';
import {
  setPendingQuery,
  setSkipHistoryLoad,
  resetMessagesState,
  resetTemplateState,
} from '@/features/chat/redux';
import { resetSessionState } from '@/features/auth/redux';
import { useRouter } from '@/shared/router/RouterContext';
import { ROUTES } from '@shared/constants';
import { useVoiceRecording } from '@/shared/hooks/useVoiceRecording';
import { clearSessionId, sanitizePasteIntoTextarea } from '@/shared/utils';
import { SAMPLE_PROMPTS, TYPING_ANIMATION_CONFIG, MAX_WORD_COUNT } from '@/features/map/constants';
import { logClick, logPageLoad, TRACKING_IDS } from '@shared/analytics';
import { ENABLE_VOICE_INPUT } from '@/features/chat/constants/featureFlags';

const MIN_HEIGHT = 24;
const MAX_HEIGHT = 120;

function countWords(text: string): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

function truncateToWordLimit(text: string, maxWords: number): string {
  const words = text.trim().split(/\s+/);
  if (words.length <= maxWords) return text;
  return words.slice(0, maxWords).join(' ');
}

function useTypingPlaceholder(prompts: readonly string[]): string {
  const [text, setText] = useState('');
  const stateRef = useRef({ promptIdx: 0, charIdx: 0, isDeleting: false });

  useEffect(() => {
    if (prompts.length === 0) return;

    let timeoutId: ReturnType<typeof setTimeout>;

    const tick = (): void => {
      const { promptIdx, charIdx, isDeleting } = stateRef.current;
      const prompt = prompts[promptIdx % prompts.length];
      if (!prompt) return;

      if (!isDeleting) {
        if (charIdx < prompt.length) {
          stateRef.current.charIdx++;
          setText(prompt.slice(0, charIdx + 1));
          timeoutId = setTimeout(tick, TYPING_ANIMATION_CONFIG.typingSpeed);
        } else {
          stateRef.current.isDeleting = true;
          timeoutId = setTimeout(tick, TYPING_ANIMATION_CONFIG.pauseAfterTyped);
        }
      } else {
        if (charIdx > 0) {
          stateRef.current.charIdx--;
          setText(prompt.slice(0, charIdx - 1));
          timeoutId = setTimeout(tick, TYPING_ANIMATION_CONFIG.deletingSpeed);
        } else {
          stateRef.current.isDeleting = false;
          stateRef.current.promptIdx = (promptIdx + 1) % prompts.length;
          timeoutId = setTimeout(tick, TYPING_ANIMATION_CONFIG.pauseAfterDeleted);
        }
      }
    };

    timeoutId = setTimeout(tick, TYPING_ANIMATION_CONFIG.pauseAfterDeleted);

    return () => clearTimeout(timeoutId);
  }, [prompts]);

  return text;
}

export function OnboardingChatInput(): React.JSX.Element {
  const dispatch = useAppDispatch();
  const { navigate } = useRouter();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const lastValidValueRef = useRef('');
  const [isFocused, setIsFocused] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const animatedPlaceholder = useTypingPlaceholder(SAMPLE_PROMPTS);

  useEffect(() => {
    logPageLoad({ prop54: TRACKING_IDS.SUGGESTED_PROMPTS_SHOWN });
  }, []);

  const {
    state: recordingState,
    partialText,
    finalText,
    error: voiceError,
    autoSubmit,
    startRecording,
    stopRecording,
    reset: resetVoice,
  } = useVoiceRecording();

  const isRecording = recordingState === 'recording';
  const isConnecting = recordingState === 'connecting';
  const isVoiceActive = isRecording || isConnecting;

  const adjustTextareaHeight = useCallback(() => {
    const textarea = inputRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const newHeight = Math.min(Math.max(textarea.scrollHeight, MIN_HEIGHT), MAX_HEIGHT);
    textarea.style.height = `${newHeight}px`;
  }, []);

  useEffect(() => {
    if (inputRef.current && partialText) {
      const truncated = truncateToWordLimit(partialText, MAX_WORD_COUNT);
      inputRef.current.value = truncated;
      lastValidValueRef.current = truncated;
      setWordCount(countWords(truncated));
      setIsFocused(true);
      adjustTextareaHeight();

      if (countWords(partialText) >= MAX_WORD_COUNT) {
        stopRecording();
      }
    }
  }, [partialText, adjustTextareaHeight, stopRecording]);

  useEffect(() => {
    if (finalText && inputRef.current) {
      const truncated = truncateToWordLimit(finalText, MAX_WORD_COUNT);
      inputRef.current.value = truncated;
      lastValidValueRef.current = truncated;
      setWordCount(countWords(truncated));
      setIsFocused(true);
      adjustTextareaHeight();

      if (autoSubmit) {
        logClick({ prop54: TRACKING_IDS.VOICE_INPUT_RECEIVED });
        clearSessionId();
        dispatch(resetSessionState());
        dispatch(resetMessagesState());
        dispatch(resetTemplateState());
        dispatch(setPendingQuery(truncated));
        dispatch(setSkipHistoryLoad(true));
        navigate(ROUTES.CHAT);
        inputRef.current.value = '';
        inputRef.current.style.height = `${MIN_HEIGHT}px`;
        lastValidValueRef.current = '';
        setWordCount(0);
        setIsFocused(false);
        resetVoice();
      }
    }
  }, [finalText, autoSubmit, dispatch, resetVoice, adjustTextareaHeight, navigate]);

  const handleSend = useCallback(() => {
    const message = inputRef.current?.value?.trim() ?? '';
    if (!message) return;

    if (isVoiceActive) {
      stopRecording();
    }

    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.style.height = `${MIN_HEIGHT}px`;
    }
    lastValidValueRef.current = '';
    setWordCount(0);
    setIsFocused(false);

    clearSessionId();
    dispatch(resetSessionState());
    dispatch(resetMessagesState());
    dispatch(resetTemplateState());
    dispatch(setPendingQuery(message));
    dispatch(setSkipHistoryLoad(true));
    navigate(ROUTES.CHAT);
  }, [dispatch, navigate, isVoiceActive, stopRecording]);

  const isAtLimit = wordCount >= MAX_WORD_COUNT;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
      return;
    }

    if (e.key === '<' || e.key === '>') {
      e.preventDefault();
      return;
    }

    if (isAtLimit) {
      const isShortcut = e.ctrlKey || e.metaKey;
      const isAllowed = new Set([
        'Backspace',
        'Delete',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
        'Tab',
        'Escape',
      ]);

      if (!isAllowed.has(e.key) && !isShortcut) {
        e.preventDefault();
      }
    }
  };

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      e.preventDefault();
      const textarea = inputRef.current;
      if (!textarea) return;

      const result = sanitizePasteIntoTextarea(textarea, e.clipboardData, MAX_WORD_COUNT);
      if (!result) return;

      lastValidValueRef.current = result.value;
      setWordCount(result.wordCount);
      setIsFocused(true);
      adjustTextareaHeight();
    },
    [adjustTextareaHeight],
  );

  const handleInput = (): void => {
    const textarea = inputRef.current;
    if (!textarea) return;

    const stripped = textarea.value.replace(/[<>]/g, '');
    if (stripped !== textarea.value) {
      const cursorPos = textarea.selectionStart;
      const diff = textarea.value.length - stripped.length;
      textarea.value = stripped;
      textarea.setSelectionRange(Math.max(0, cursorPos - diff), Math.max(0, cursorPos - diff));
    }

    const words = countWords(textarea.value);
    if (words > MAX_WORD_COUNT) {
      const cursorPos = textarea.selectionStart;
      textarea.value = lastValidValueRef.current;
      const newPos = Math.min(cursorPos, textarea.value.length);
      textarea.setSelectionRange(newPos, newPos);
    } else {
      lastValidValueRef.current = textarea.value;
    }

    setWordCount(countWords(textarea.value));
    adjustTextareaHeight();
  };

  const handleFocus = useCallback(() => setIsFocused(true), []);
  const handleBlur = useCallback(() => {
    if (!inputRef.current?.value) setIsFocused(false);
  }, []);

  const handleVoiceClick = useCallback(() => {
    logClick({ prop54: TRACKING_IDS.VOICE_CLICKED });
    if (isVoiceActive) {
      stopRecording();
    } else {
      void startRecording('en-IN');
    }
  }, [isVoiceActive, startRecording, stopRecording]);

  const getVoiceButtonStyles = (): string => {
    const baseStyles =
      'absolute top-1/2 right-[60px] inline-flex h-10 w-10 -translate-y-1/2 cursor-pointer border-0 outline-none transition-all duration-200';

    switch (recordingState) {
      case 'connecting':
        return `${baseStyles} bg-yellow-100 rounded-full animate-pulse`;
      case 'recording':
        return `${baseStyles} bg-red-100 rounded-full animate-pulse`;
      case 'error':
        return `${baseStyles} bg-red-200 rounded-full`;
      default:
        return `${baseStyles} bg-transparent`;
    }
  };

  const renderVoiceIndicator = (): React.JSX.Element | null => {
    if (isRecording) {
      return (
        <span className="bg-error absolute -top-1 -right-1 h-3 w-3 animate-pulse rounded-full" />
      );
    }
    if (isConnecting) {
      return (
        <span className="bg-warning absolute -top-1 -right-1 h-3 w-3 animate-pulse rounded-full" />
      );
    }
    return null;
  };

  return (
    <div className="relative flex flex-col gap-1">
      {ENABLE_VOICE_INPUT && voiceError && (
        <div className="bg-error-bg text-error mx-4 rounded-lg px-3 py-1.5 text-xs">
          {voiceError}
        </div>
      )}

      {ENABLE_VOICE_INPUT && isVoiceActive && (
        <div className="bg-primary-light text-link mx-4 flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs">
          <span
            className={`h-2 w-2 rounded-full ${isRecording ? 'bg-error animate-pulse' : 'bg-warning animate-pulse'}`}
          />
          {isConnecting ? 'Connecting to voice service...' : 'Listening... Speak now'}
        </div>
      )}

      {/* TODO: Add word count indicator */}
      {/* {wordCount > 0 && (
        <p
          className={`label-sm-w2 absolute top-[-20px] right-[10px] ${
            wordCount >= MAX_WORD_COUNT
              ? 'text-error'
              : wordCount >= MAX_WORD_COUNT - 30
                ? 'text-warning'
                : 'text-low-emphasis'
          }`}
        >
          {wordCount}/{MAX_WORD_COUNT} words
        </p>
      )} */}

      <div className="bg-gradient-button relative flex w-full items-center rounded-[32px] p-[5px]">
        <div className="relative flex min-h-full w-full items-center rounded-[27px] bg-white px-1 py-3">
          {!isFocused && !isVoiceActive && (
            <div
              className="font-avenir pointer-events-none absolute top-1/2 left-[16px] line-clamp-1 w-[calc(100%-126px)] -translate-y-1/2 text-[16px] leading-[24px]"
              aria-hidden="true"
            >
              <span className="text-[#4a4a4a]">Try asking </span>
              {animatedPlaceholder.length > 0 && (
                <>
                  <span className="text-black">&ldquo;</span>
                  <span className="text-black italic">{animatedPlaceholder}</span>
                  <span className="text-black italic">&rdquo;</span>
                </>
              )}
            </div>
          )}
          <textarea
            ref={inputRef}
            placeholder={isRecording ? 'Listening...' : undefined}
            className="font-avenir text-high-emphasis placeholder:text-low-emphasis w-full resize-none border-none pr-[110px] pl-[16px] text-[16px] leading-[24px] outline-none"
            style={{ height: `${MIN_HEIGHT}px`, maxHeight: `${MAX_HEIGHT}px` }}
            onKeyDown={handleKeyDown}
            onInput={handleInput}
            onPaste={handlePaste}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={isRecording}
            rows={1}
          />
        </div>
        {ENABLE_VOICE_INPUT && (
          <button
            onClick={handleVoiceClick}
            className={getVoiceButtonStyles()}
            title={isVoiceActive ? 'Stop recording' : 'Start voice input'}
          >
            <div className="relative flex items-center justify-center">
              <img
                src={icons.VoiceIcon}
                alt="voice"
                className={`h-10 w-10 object-contain ${isRecording ? 'opacity-80' : ''}`}
              />
              {renderVoiceIndicator()}
            </div>
          </button>
        )}
        <button
          onClick={handleSend}
          className="absolute top-1/2 right-[9px] inline-flex h-10 w-10 -translate-y-1/2 cursor-pointer border-0 bg-transparent outline-none"
        >
          <img src={icons.EnterIcon} alt="submit" className="h-10 w-10" />
        </button>
      </div>
    </div>
  );
}
